import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Loader2, Shield, Trash2 } from 'lucide-react';
import { useOfferPermissions } from '../../hooks/permissionHooks/useOfferPermissions';

const DeleteOfferModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  offerTitle, 
  isDeleting 
}) => {
  const { 
    canDeleteOffers, 
    getPermissionInfo,
    isMainVendor 
  } = useOfferPermissions();
  
  const deletePermissionInfo = getPermissionInfo('delete');

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed top-0 right-0 bottom-0 left-0 z-50 min-h-screen"
    >
      {/* Overlay */}
      <div 
        className="fixed top-0 left-0 right-0 bottom-0 bg-black/30 backdrop-blur-[2px]" 
        onClick={onClose} 
      />
      
      {/* Modal */}
      <div className="flex fixed top-0 right-0 bottom-0 left-0 justify-center items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white rounded-3xl w-[420px] mx-auto shadow-xl"
        >
          <div className="px-8 py-6">
            {canDeleteOffers ? (
              <div className="flex flex-col items-center">
                <div className="flex justify-center items-center mb-4 w-16 h-16 bg-red-100 rounded-full">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-[24px] font-bold text-gray-900 mb-2">Delete Offer</h3>
                <p className="mb-1 text-center text-gray-600">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">"{offerTitle}"</span>?
                </p>
                <p className="text-red-500 text-[14px] text-center mb-4 font-medium">This action cannot be undone.</p>
                
                {/* Warning for data impact */}
                <div className="p-4 mb-6 w-full bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Impact Warning</span>
                  </div>
                  <p className="text-sm text-amber-800">
                    This will permanently remove the offer and all associated campaign data, analytics, and redemption history.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="flex justify-center items-center mb-4 w-16 h-16 bg-red-100 rounded-full">
                  <Shield className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-[24px] font-bold text-gray-900 mb-2">Permission Required</h3>
                <p className="mb-4 text-center text-gray-600">
                  You don't have permission to delete offers. Please contact your administrator to request this permission.
                </p>
                
                {/* Permission details */}
                <div className="p-4 mb-4 w-full bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">Required Permission</span>
                  </div>
                  <p className="text-sm text-blue-800">
                    {deletePermissionInfo?.description || "Delete Offer in Deals Management"}
                  </p>
                </div>

                {/* User status info */}
                <div className="p-3 w-full bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-600 text-center">
                    Current Role: {isMainVendor ? "Main Vendor" : "Team Member"} • 
                    Status: Limited Access
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 justify-end items-center mt-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="px-6 py-3 text-gray-700 font-medium rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-200"
              >
                {canDeleteOffers ? 'Cancel' : 'Close'}
              </motion.button>
              
              {canDeleteOffers && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onConfirm}
                  disabled={isDeleting}
                  className="px-6 py-3 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[120px] justify-center"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Offer
                    </>
                  )}
                </motion.button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default DeleteOfferModal; 